import { createSlice } from '@reduxjs/toolkit';
import { withDealPricing, dealPricingFields } from '../utils/vendorDiscounts';
import {
    acceptQuotedPrice,
    applyQuote,
    branchProductChanged,
    lineFieldsFor,
    markLinesChanged,
    markLinesForMove,
    markLinesUnchecked,
    oneLocationCart,
    placeCartLine,
    setLineOutlet,
    withoutBranchRead,
} from '../utils/branchCart';

const initialState = {
    currentRestaurant: null,
    carts: {},
    restaurant: null,
    shippingAddress: {},
    paymentMethod: null,
    total: 0,
};

const calculateTotal = (carts, restaurantId) => {
    const cart = carts[restaurantId] || {};
    return Object.values(cart).reduce((total, item) => {
        const optionsTotal = item.options.reduce((sum, option) => sum + option.price, 0);
        return total + (item.price + optionsTotal) * item.quantity;
    }, 0);
};

/* A cart read back from localStorage is checked again at its branch before
   the shopper pays (4 Oct 2026). A basket saved across two branches of one
   restaurant, which the website used to allow, is put on one: the order is
   cooked and dispatched from one kitchen. */
const loadCartFromLocalStorage = () => {
    const savedCart = localStorage.getItem('restaurantCart');
    const carts = savedCart ? JSON.parse(savedCart) : {};
    return Object.fromEntries(
        Object.entries(carts || {}).map(([restaurantId, cart]) => [
            restaurantId,
            markLinesUnchecked(oneLocationCart(cart)),
        ])
    );
};

/* ONE LOCATION PER RESTAURANT CART (4 Oct 2026): utils/branchCart
   placeCartLine. A line from another branch moves the whole cart there (the
   storefront asks first), so a mixed-branch basket, which sent half an order
   to the wrong kitchen, can no longer be built. */
const placeLine = (state, restaurantId, payload) => {
    const placed = placeCartLine(state.carts[restaurantId], payload);
    state.carts[restaurantId] = placed.cart;
    return placed.outletId;
};

// The current-vendor pointer must survive a full page reload just like the
// cart items do. Without this, a reload mid-checkout leaves carts populated
// but currentRestaurant=null — the payment screen's empty-cart guard then
// redirected to /restaurant/null (the mobile deep-link route, which shows
// "Opening Play Store…"), and the shipping screen's coverage check silently
// couldn't run (no vendorId), letting out-of-range addresses through.
const loadCurrentRestaurantFromLocalStorage = () => {
    try {
        return localStorage.getItem('restaurantCurrentVendor') || null;
    } catch (e) {
        return null;
    }
};

const saveCurrentRestaurantToLocalStorage = (id) => {
    try {
        if (id) {
            localStorage.setItem('restaurantCurrentVendor', id);
        } else {
            localStorage.removeItem('restaurantCurrentVendor');
        }
    } catch (e) {
        // Storage unavailable (private mode etc.) — in-memory state still works.
    }
};

const loadTotalFromLocalStorage = () => {
    const savedTotal = localStorage.getItem('restaurantTotal');
    return savedTotal ? JSON.parse(savedTotal) : 0;
};

const saveCartToLocalStorage = (carts) => {
    localStorage.setItem('restaurantCart', JSON.stringify(carts));
};

const saveTotalToLocalStorage = (total) => {
    localStorage.setItem('restaurantTotal', JSON.stringify(total));
};

const restaurantCartSlice = createSlice({
    name: 'restaurantCart',
    initialState: {
        ...initialState,
        carts: loadCartFromLocalStorage(),
        total: loadTotalFromLocalStorage(),
        currentRestaurant: loadCurrentRestaurantFromLocalStorage(),
    },
    reducers: {
        addToCart: (state, action) => {
            const { restaurantId, product, quantity, options = [] } = action.payload;

            if (!state.carts[restaurantId]) {
                state.carts[restaurantId] = {};
            }
            const lineOutletId = placeLine(state, restaurantId, action.payload);

            // Vendor discounts: the line's `price` is what the customer pays
            // (salePrice while an offer is live), with listPrice and the
            // discount kept beside it for the strike-through and savings line.
            const isNewLine = !state.carts[restaurantId][product._id];
            if (isNewLine) {
                state.carts[restaurantId][product._id] = { ...withDealPricing(withoutBranchRead(product)), quantity: 0, options: [] };
            }

            const productEntry = state.carts[restaurantId][product._id];
            Object.assign(productEntry, dealPricingFields(product));
            setLineOutlet(productEntry, lineOutletId);
            // A dish read at this very branch confirms the line at the price
            // just copied onto it; anything else is checked before paying.
            const branchFields = lineFieldsFor(product, lineOutletId);
            if (isNewLine || !branchFields.needsCheck) {
                Object.assign(productEntry, branchFields);
            }
            productEntry.quantity += quantity;

            if (quantity > 0) {
                productEntry.options = options;
            }

            state.currentRestaurant = restaurantId;
            saveCurrentRestaurantToLocalStorage(restaurantId);
            state.total = calculateTotal(state.carts, restaurantId);
            saveCartToLocalStorage(state.carts);
            saveTotalToLocalStorage(state.total);
        },
        setCartItemQuantity: (state, action) => {
            const { restaurantId, product, quantity, options = [] } = action.payload;

            if (!state.carts[restaurantId]) {
                state.carts[restaurantId] = {};
            }

            if (quantity <= 0) {
                delete state.carts[restaurantId][product._id];
            } else {
                const lineOutletId = placeLine(state, restaurantId, action.payload);
                const previous = state.carts[restaurantId][product._id];
                const branchFields = lineFieldsFor(product, lineOutletId);
                const entry = {
                    ...withDealPricing(withoutBranchRead(product)),
                    quantity,
                    options,
                    // An existing line keeps its check flags unless this read
                    // of the dish confirms it at this branch.
                    ...(previous && branchFields.needsCheck
                        ? {
                              needsCheck: previous.needsCheck,
                              branchStatus: previous.branchStatus ?? null,
                              quotedPrice: previous.quotedPrice ?? null,
                              quotedListPrice: previous.quotedListPrice ?? null,
                          }
                        : branchFields),
                };
                setLineOutlet(entry, lineOutletId);
                state.carts[restaurantId][product._id] = entry;
            }

            state.currentRestaurant = restaurantId;
            saveCurrentRestaurantToLocalStorage(restaurantId);
            state.total = calculateTotal(state.carts, restaurantId);
            saveCartToLocalStorage(state.carts);
            saveTotalToLocalStorage(state.total);
        },
        removeFromCart: (state, action) => {
            const { restaurantId, productId } = action.payload;
            if (state.carts[restaurantId] && state.carts[restaurantId][productId]) {
                delete state.carts[restaurantId][productId];
            }
            state.total = calculateTotal(state.carts, restaurantId);
            saveCartToLocalStorage(state.carts);
            saveTotalToLocalStorage(state.total);
        },
        setCurrentRestaurant: (state, action) => {
            state.currentRestaurant = action.payload;
            saveCurrentRestaurantToLocalStorage(action.payload);
            // Recalculate total for the new current restaurant
            state.total = calculateTotal(state.carts, action.payload);
            saveTotalToLocalStorage(state.total);
        },
        switchRestaurant: (state, action) => {
            state.currentRestaurant = action.payload;
            saveCurrentRestaurantToLocalStorage(action.payload);
            // Recalculate total for the new current restaurant
            state.total = calculateTotal(state.carts, action.payload);
            saveTotalToLocalStorage(state.total);
        },
        setRestaurantData: (state, action) => {
            const restaurant = action.payload;
            state.restaurant = restaurant;
            state.currentRestaurant = restaurant.owner; // Set currentRestaurant to owner value
            saveCurrentRestaurantToLocalStorage(restaurant.owner);
            // Recalculate total for the current restaurant's cart
            state.total = calculateTotal(state.carts, restaurant.owner);
            saveTotalToLocalStorage(state.total);
        },
        saveShippingAddress: (state, action) => {
            state.shippingAddress = action.payload;
        },
        savePaymentMethod: (state, action) => {
            state.paymentMethod = action.payload;
        },
        /* The shopper chose another branch of this restaurant on the
           storefront (the location picker or "Get it from"). The cart follows
           them and every line is checked there before they pay. */
        moveCartToOutlet: (state, action) => {
            const { restaurantId, outletId = null } = action.payload || {};
            const cart = restaurantId ? state.carts[restaurantId] : null;
            if (!cart || !Object.keys(cart).length) return;
            state.carts[restaurantId] = markLinesForMove(cart, outletId);
            saveCartToLocalStorage(state.carts);
        },
        /* The cart check's verdicts (hooks/useBranchCartCheck). */
        applyBranchQuote: (state, action) => {
            const { cartId, verdicts } = action.payload || {};
            const cart = cartId ? state.carts[cartId] : null;
            if (!cart) return;
            const next = applyQuote(cart, verdicts);
            if (next === cart) return;
            state.carts[cartId] = next;
            saveCartToLocalStorage(state.carts);
        },
        /* "Keep at new price" in the review: the line takes the branch price. */
        acceptBranchPrice: (state, action) => {
            const { cartId, productId } = action.payload || {};
            const cart = cartId ? state.carts[cartId] : null;
            if (!cart) return;
            state.carts[cartId] = acceptQuotedPrice(cart, productId);
            state.total = calculateTotal(state.carts, state.currentRestaurant || cartId);
            saveCartToLocalStorage(state.carts);
            saveTotalToLocalStorage(state.total);
        },
        clearCart: (state, action) => {
            const restaurantId = action.payload;
            if (state.carts[restaurantId]) {
                delete state.carts[restaurantId];
                state.total = calculateTotal(state.carts, restaurantId);
                saveCartToLocalStorage(state.carts);
                saveTotalToLocalStorage(state.total);
            }
        },
        resetRestaurantCart: (state) => {
            state.currentRestaurant = null;
            saveCurrentRestaurantToLocalStorage(null);
            state.carts = {};
            state.restaurant = null;
            state.shippingAddress = {};
            state.paymentMethod = null;
            state.total = 0;
            saveCartToLocalStorage({});
            saveTotalToLocalStorage(0);
        },
    },
    extraReducers: (builder) => {
        /* A branch changed one dish live (catalog:public): the lines it
           touches are checked again before Place order. */
        builder.addCase(branchProductChanged, (state, action) => {
            let changed = false;
            Object.keys(state.carts).forEach((restaurantId) => {
                const cart = state.carts[restaurantId];
                const next = markLinesChanged(cart, action.payload || {});
                if (next !== cart) {
                    state.carts[restaurantId] = next;
                    changed = true;
                }
            });
            if (changed) saveCartToLocalStorage(state.carts);
        });
    },
});

export const {
    addToCart,
    setCartItemQuantity,
    removeFromCart,
    setCurrentRestaurant,
    switchRestaurant,
    setRestaurantData,
    saveShippingAddress,
    savePaymentMethod,
    clearCart,
    moveCartToOutlet,
    applyBranchQuote,
    acceptBranchPrice,
    resetRestaurantCart,
    confirmVendorSwitch,
    cancelVendorSwitch,
} = restaurantCartSlice.actions;

export default restaurantCartSlice.reducer;
